import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { sendChatMessage } from '../services/api';
import { 
  Shield, ArrowLeft, Send, Bot, User, Sparkles, 
  MessageSquare, Lightbulb, AlertTriangle, BookOpen
} from 'lucide-react';

const SUGGESTED_PROMPTS = [
  { icon: Lightbulb, text: "How can I improve our district's maturity level?", category: "improvement" },
  { icon: AlertTriangle, text: "What are common K-12 risk blind spots?", category: "awareness" },
  { icon: BookOpen, text: "Explain the 10 Skills framework", category: "learning" },
  { icon: MessageSquare, text: "How do I build a risk-aware culture?", category: "culture" },
];

export default function ChatPage() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async (messageText) => {
    const text = messageText || input.trim();
    if (!text || loading) return;

    const userMessage = { role: 'user', content: text };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await sendChatMessage(text, sessionId);
      setSessionId(response.data.session_id);
      setMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'I apologize, but I encountered an error. Please try again.',
        error: true 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="bg-surface border-b border-border px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <Link to="/dashboard" className="p-2 hover:bg-slate-100 rounded-md transition-all">
            <ArrowLeft className="w-5 h-5 text-text-muted" />
          </Link>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-accent-blue flex items-center justify-center">
              <Bot className="w-6 h-6 text-white" />
            </div>
            <div>
              <span className="font-outfit text-xl font-bold text-primary">RiskShield AI</span>
              <p className="text-xs text-text-muted">Your K-12 Risk Intelligence Assistant</p>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-4xl w-full mx-auto flex flex-col">
        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {messages.length === 0 ? (
            <div className="text-center py-12 animate-in" data-testid="chat-welcome">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-accent-blue/10 flex items-center justify-center">
                <Sparkles className="w-10 h-10 text-accent-blue" />
              </div>
              <h2 className="font-outfit text-2xl font-bold text-primary mb-2">
                Welcome to RiskShield AI
              </h2>
              <p className="text-text-muted max-w-md mx-auto mb-8">
                I'm your AI assistant specialized in K-12 risk intelligence. Ask me anything about 
                risk management, the 10 Skills framework, or how to improve your district's maturity level.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
                {SUGGESTED_PROMPTS.map((prompt, index) => (
                  <button
                    key={index}
                    onClick={() => handleSend(prompt.text)}
                    className="flex items-start gap-3 p-4 bg-surface border border-border rounded-lg text-left hover:border-accent-blue hover:bg-blue-50 transition-all"
                    data-testid={`suggested-prompt-${index}`}
                  >
                    <prompt.icon className="w-5 h-5 text-accent-blue flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-primary">{prompt.text}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((message, index) => (
              <div
                key={index}
                className={`flex gap-4 animate-in ${message.role === 'user' ? 'justify-end' : ''}`}
                data-testid={`chat-message-${index}`}
              >
                {message.role === 'assistant' && (
                  <div className="w-10 h-10 rounded-full bg-accent-blue flex-shrink-0 flex items-center justify-center">
                    <Bot className="w-5 h-5 text-white" />
                  </div>
                )}
                <div
                  className={`max-w-[80%] rounded-lg p-4 ${
                    message.role === 'user'
                      ? 'bg-primary text-white'
                      : message.error
                      ? 'bg-red-50 border border-red-200 text-risk-critical'
                      : 'bg-surface border border-border text-primary'
                  }`}
                >
                  <p className="whitespace-pre-wrap leading-relaxed">{message.content}</p>
                </div>
                {message.role === 'user' && (
                  <div className="w-10 h-10 rounded-full bg-primary flex-shrink-0 flex items-center justify-center">
                    <User className="w-5 h-5 text-white" />
                  </div>
                )}
              </div>
            ))
          )}
          
          {loading && (
            <div className="flex gap-4 animate-in">
              <div className="w-10 h-10 rounded-full bg-accent-blue flex-shrink-0 flex items-center justify-center">
                <Bot className="w-5 h-5 text-white" />
              </div>
              <div className="bg-surface border border-border rounded-lg p-4">
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="border-t border-border bg-surface p-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex gap-4">
              <div className="flex-1 relative">
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Ask about K-12 risk management..."
                  className="w-full px-4 py-3 pr-12 border border-border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-accent-blue focus:border-transparent transition-all"
                  rows={1}
                  disabled={loading}
                  data-testid="chat-input"
                />
              </div>
              <button
                onClick={() => handleSend()}
                disabled={!input.trim() || loading}
                className="px-6 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
                data-testid="chat-send"
              >
                <Send className="w-5 h-5" />
                <span className="hidden sm:inline">Send</span>
              </button>
            </div>
            <p className="text-xs text-text-muted mt-2 text-center">
              RiskShield AI is trained on the 10 Skills of School Risk Intelligence framework
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
